/**
 * @fileoverview Syndicator Showdown — Firestore Client Read/Write Helpers
 * @module services/firestore
 *
 * Typed service functions for all Firestore operations in the web client.
 * All data is sanitized (Timestamps → ISO strings) before being returned
 * so React components never receive raw Firestore objects.
 *
 * ─── PATTERNS ─────────────────────────────────────────────────────────────────
 *
 * One-time reads:    fetch*()      → Promise<T>
 * Real-time streams: subscribe*()  → unsubscribe function (call on unmount)
 */

import {
  doc,
  collection,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  limit,
} from 'firebase/firestore';

import { db } from './firebase';

// ─── Timestamp Sanitiser ──────────────────────────────────────────────────────

/**
 * Recursively converts Firestore Timestamps to ISO strings in any object.
 * Also cleans up any undefined values that Firestore may return.
 *
 * @param {*} value
 * @returns {*} Plain JavaScript value
 */
function sanitise(value) {
  if (value === null || value === undefined) return null;

  // Firestore Timestamp
  if (typeof value?.toDate === 'function') {
    return value.toDate().toISOString();
  }

  if (Array.isArray(value)) {
    return value.map(sanitise);
  }

  if (typeof value === 'object') {
    const cleaned = {};
    Object.entries(value).forEach(([k, v]) => {
      cleaned[k] = sanitise(v);
    });
    return cleaned;
  }

  return value;
}

// ─── Slate Reads (Lobby & Card Streams) ───────────────────────────────────────

/**
 * Real-time subscription to all active tournament game slates.
 * Filters for slates currently open or building to display on the player dashboard.
 *
 * @param {Function} onUpdate - Callback fired on snapshot updates
 * @returns {Function} Unsubscribe teardown closure
 */
export function subscribeToActiveSlates(onUpdate) {
  const slatesRef = collection(db, 'game_slates');
  const slatesQuery = query(slatesRef, orderBy('meetingDate', 'asc'));

  return onSnapshot(
    slatesQuery,
    (snapshot) => {
      const slates = snapshot.docs.map((d) =>
        sanitise({ id: d.id, ...d.data() })
      );
      onUpdate(slates);
    },
    (error) => {
      console.error('[subscribeToActiveSlates] Error streaming lobby configurations:', error);
    }
  );
}

/**
 * Loads a complete slate with all races and their runners in one call.
 * Reads are parallelised: all races fetched simultaneously, then all
 * runner sub-collections fetched simultaneously.
 *
 * @param {string} slateId
 * @returns {Promise<{ slate: Object, races: Array }>}
 * @throws {Error} If slate not found or Firestore error
 */
export async function fetchSlateWithRaces(slateId) {
  // ── 1. Read slate document ──────────────────────────────────────────────
  const slateRef = doc(db, 'game_slates', slateId);
  const slateSnap = await getDoc(slateRef);

  if (!slateSnap.exists()) {
    throw new Error(`Slate not found: ${slateId}`);
  }

  const slate = sanitise({ id: slateSnap.id, ...slateSnap.data() });

  // ── 2. Read races sub-collection (ordered by raceNumber) ─────────────────
  const racesQuery = query(
    collection(db, 'game_slates', slateId, 'races'),
    orderBy('raceNumber', 'asc')
  );
  const racesSnap = await getDocs(racesQuery);

  if (racesSnap.empty) {
    return { slate, races: [] };
  }

  // Runners are stored as an array field inside each race document —
  // there is no runners sub-collection. Map directly from the race docs.
  const races = racesSnap.docs.map((d) => sanitise({ id: d.id, ...d.data() }));

  return { slate, races };
}

/**
 * Real-time subscription to race status changes within a slate.
 * Fires whenever any race updates — used to update `isLocked` on RaceCards
 * as races jump on Saturday afternoon.
 *
 * @param {string}   slateId
 * @param {Function} onUpdate - Called with updated races array on any change
 * @returns {Function} Unsubscribe function — call in useEffect cleanup
 */
export function subscribeToRaceStatuses(slateId, onUpdate) {
  const racesQuery = query(
    collection(db, 'game_slates', slateId, 'races'),
    orderBy('raceNumber', 'asc')
  );

  return onSnapshot(
    racesQuery,
    (snapshot) => {
      const races = snapshot.docs.map((d) =>
        sanitise({ id: d.id, ...d.data() })
      );
      onUpdate(races);
    },
    (error) => {
      console.error('[subscribeToRaceStatuses] Snapshot error:', error);
    }
  );
}

/**
 * Real-time subscription to a single slate document.
 * Used to detect when entriesOpen flips to true after Thursday lock.
 *
 * @param {string}   slateId
 * @param {Function} onUpdate - Called with updated slate data
 * @returns {Function} Unsubscribe function
 */
export function subscribeToSlate(slateId, onUpdate) {
  return onSnapshot(
    doc(db, 'game_slates', slateId),
    (snap) => {
      if (snap.exists()) {
        onUpdate(sanitise({ id: snap.id, ...snap.data() }));
      }
    },
    (error) => {
      console.error('[subscribeToSlate] Snapshot error:', error);
    }
  );
}

// ─── Player Entry Reads ───────────────────────────────────────────────────────

/**
 * Real-time subscription to a player's submitted stables history.
 * Filters by userId at the query level so Firestore security rules
 * (which check resource.data.userId == request.auth.uid) are satisfied.
 *
 * @param {string}   userId   - Authenticated user UID
 * @param {Function} onUpdate - Called with the updated entries array
 * @param {Function} onError  - Optional; called with the Error object on failure
 * @returns {Function} Unsubscribe function — call on unmount
 */
export function subscribeToPlayerStables(userId, onUpdate, onError) {
  const entriesQuery = query(
    collection(db, 'player_entries'),
    where('userId', '==', userId),
    orderBy('submittedAt', 'desc')
  );

  return onSnapshot(
    entriesQuery,
    (snapshot) => {
      const history = snapshot.docs.map(d => sanitise({ id: d.id, ...d.data() }));
      onUpdate(history);
    },
    (error) => {
      console.error('[subscribeToPlayerStables] Snapshot error:', error);
      if (onError) onError(error);
    }
  );
}

/**
 * Fetches an existing player entry for a slate (if one exists).
 * Used when a player returns to edit their stable before lock.
 *
 * @param {string} slateId
 * @param {string} userId
 * @returns {Promise<Object|null>} Entry with race_allocations, or null if not found
 */
export async function fetchPlayerEntry(slateId, userId) {
  const entryId  = `${slateId}_${userId}`;
  const entryRef = doc(db, 'player_entries', entryId);
  const entrySnap = await getDoc(entryRef);

  if (!entrySnap.exists()) return null;

  // Read race_allocations sub-collection
  const allocsSnap = await getDocs(
    collection(db, 'player_entries', entryId, 'race_allocations')
  );

  const allocations = {};
  allocsSnap.docs.forEach((d) => {
    allocations[d.id] = sanitise(d.data());
  });

  return sanitise({
    id: entrySnap.id,
    ...entrySnap.data(),
    allocations,
  });
}

// ─── Leaderboard Reads ────────────────────────────────────────────────────────

/**
 * Real-time subscription to the top N leaderboard entries for a slate.
 *
 * @param {string}   slateId
 * @param {number}   topN      - Number of entries to show (default 100)
 * @param {Function} onUpdate  - Called with updated rankings array
 * @returns {Function} Unsubscribe function
 */
export function subscribeToLeaderboard(slateId, topN = 100, onUpdate) {
  const rankingsQuery = query(
    collection(db, 'leaderboards', slateId, 'full_rankings'),
    orderBy('rank', 'asc'),
    limit(topN)
  );

  return onSnapshot(
    rankingsQuery,
    (snapshot) => {
      const rankings = snapshot.docs.map((d) =>
        sanitise({ id: d.id, ...d.data() })
      );
      onUpdate(rankings);
    },
    (error) => {
      console.error('[subscribeToLeaderboard] Snapshot error:', error);
    }
  );
}

/**
 * Fetches a player's current leaderboard rank for a slate.
 *
 * @param {string} slateId
 * @param {string} userId
 * @returns {Promise<Object|null>} Rank document or null
 */
export async function fetchPlayerRank(slateId, userId) {
  const rankRef  = doc(db, 'leaderboards', slateId, 'full_rankings', userId);
  const rankSnap = await getDoc(rankRef);
  if (!rankSnap.exists()) return null;
  return sanitise({ id: rankSnap.id, ...rankSnap.data() });
}

// ─── Tournament Reads ─────────────────────────────────────────────────────────

/**
 * Fetches a tournament document.
 *
 * @param {string} tournamentId
 * @returns {Promise<Object>}
 */
export async function fetchTournament(tournamentId) {
  const snap = await getDoc(doc(db, 'tournaments', tournamentId));
  if (!snap.exists()) throw new Error(`Tournament not found: ${tournamentId}`);
  return sanitise({ id: snap.id, ...snap.data() });
}

// ─── Hash Manifest Reads (Audit) ──────────────────────────────────────────────

/**
 * Fetches the public hash manifest for a specific race.
 * Used in the integrity audit viewer.
 *
 * @param {string} slateId
 * @param {string} raceId
 * @returns {Promise<Object|null>}
 */
export async function fetchHashManifest(slateId, raceId) {
  const manifestId = `${slateId}_${raceId}_MANIFEST`;
  const snap = await getDoc(doc(db, 'hash_manifests', manifestId));
  if (!snap.exists()) return null;
  return sanitise({ id: snap.id, ...snap.data() });
}